import type { ElementType } from 'react';
import { Card, type CardProps, type CardTypeMap } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import { palette } from '../theme/palette';

export interface HairlineCardOwnProps {
  surface?: 'white' | 'cream';
}

export type HairlineCardTypeMap<D extends ElementType = 'div'> = CardTypeMap<
  HairlineCardOwnProps,
  D
>;

export type HairlineCardProps<D extends ElementType = 'div'> = CardProps<D, HairlineCardOwnProps>;

/**
 * Polymorphic over the rendered element, so a card that navigates can *be* a
 * link (`component={RouterLink} to={...}`) instead of a div wearing a button
 * role. That gets middle-click, ctrl-click, "open in new tab" and the browser's
 * URL preview for free — none of which a keydown handler can reproduce.
 *
 * Typed as MUI's `OverridableComponent` rather than a generic function: the
 * plain generic form does not infer the element type from `component`, so the
 * extra props of the target (`to`) fail to typecheck at the call site.
 *
 * Defaults to a `div`, so existing usage is unchanged.
 */
export const HairlineCard: OverridableComponent<HairlineCardTypeMap> = function HairlineCard({
  surface = 'white',
  sx,
  ...rest
}: HairlineCardProps) {
  const border = surface === 'cream' ? palette.border.onCream : palette.border.onWhite;
  const bg = surface === 'cream' ? palette.background.default : palette.background.paper;
  return (
    <Card
      elevation={0}
      sx={{ border: `1px solid ${border}`, backgroundColor: bg, borderRadius: 2, ...sx }}
      {...rest}
    />
  );
};
